import React, { useState } from "react";
import { ArrowRight } from "lucide-react";
import { IoMdClose } from "react-icons/io";
import { BASE_URL } from "utils/constant";
import { useAlert } from "components/UI_components/Alert";

const UpdateVendorModal = ({ vendor, onClose, onUpdate }) => {
  // Strip +91 prefix for the input field
  const rawContact = vendor?.contact_no?.startsWith("+91")
    ? vendor.contact_no.slice(3)
    : vendor?.contact_no || "";

  const [name, setName] = useState(vendor?.name || "");
  const [type, setType] = useState(vendor?.vendor_type || "");
  const [address, setAddress] = useState(vendor?.address || "");
  const [contactNumber, setContactNumber] = useState(rawContact);
  const [phoneError, setPhoneError] = useState("");
  const [loading, setLoading] = useState(false);
  const { addAlert } = useAlert();

  const handleNumberInput = (e) => {
    const onlyNums = e.target.value.replace(/[^0-9]/g, "");
    e.target.value = onlyNums;
    setContactNumber(onlyNums);
    if (onlyNums.length > 0 && onlyNums.length < 10) {
      setPhoneError("Contact number must be exactly 10 digits");
    } else {
      setPhoneError("");
    }
  };

  const handleSubmit = async () => {
    if (contactNumber.length !== 10) {
      setPhoneError("Contact number must be exactly 10 digits");
      return;
    }
    setPhoneError("");
    setLoading(true);

    try {
      const accessToken = localStorage.getItem("access");

      const response = await fetch(
        `${BASE_URL}/inventory/update-vendor/${vendor.id}/`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            name,
            vendor_type: type,
            address,
            contact_no: "+91" + contactNumber,
          }),
        },
      );

      const data = await response.json();

      if (response.ok && data.status) {
        onUpdate();
        onClose();
        addAlert("Vendor updated successfully", "success");
      } else {
        addAlert(data.message || "Failed to update vendor", "error");
      }
    } catch (err) {
      addAlert("An error occurred while updating vendor", "error");
      console.error("Error updating vendor:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[black] bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-[15px] w-[40vw] relative">
        {/* Header */}
        <div className="flex justify-between items-center mb-6 border-b-[0.5px] border-gray-300 py-3 px-6">
          <div></div>
          <div className="text-[#232323] text-lg font-medium">
            Update Vendor
          </div>
          <button
            className="text-2xl font-semibold text-gray-500 hover:text-black"
            onClick={onClose}
          >
            <IoMdClose size={24} />
          </button>
        </div>

        {/* General Details Label */}
        <div className="text-[#232323] text-lg font-semibold mb-6 px-4">
          General Details
        </div>

        <div className="space-y-4 py-3 px-4">
          {/* Vendor Name */}
          <div>
            <label className="block text-[#000000] text-[16px] mb-1">
              Vendor Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter Name"
              className="w-full px-4 py-2 rounded-md bg-[#F5F5F5] outline-none"
            />
          </div>

          {/* Vendor Type with chips */}
          <div>
            <label className="block text-[#000000] text-[16px] mb-1">
              Select type
            </label>
            <input
              type="text"
              value={type}
              onChange={(e) => setType(e.target.value)}
              placeholder="Enter Vendor type. (Eg. Brick supplier)"
              className="w-full px-4 py-2 rounded-md bg-[#F5F5F5] outline-none"
            />
            <div className="mt-2 flex flex-wrap gap-2 px-2 py-1">
              {[
                "Cement Supplier",
                "Steel Supplier",
                "Brick Supplier",
                "Electricals",
                "Plumbing",
              ].map((option) => (
                <button
                  key={option}
                  onClick={() => setType(option)}
                  className={`px-3 py-1 rounded-full text-sm ${
                    type === option
                      ? "bg-[#ECE6F0] text-[#3B0069] border-[1px] border-[#3B0069]"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="block text-[#000000] text-[16px] mb-1">
              Address
            </label>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Enter Address. (Saltlake, Kolkata, 700000)"
              className="w-full px-4 py-2 rounded-md bg-[#F5F5F5] outline-none"
            />
          </div>

          {/* Contact Number */}
          <div>
            <label className="block text-[#000000] text-[16px] mb-1">
              Contact number
            </label>
            <div
              className={`flex items-center bg-[#F5F5F5] px-4 py-2 rounded-md border ${
                phoneError ? "border-red-500" : "border-transparent"
              }`}
            >
              <span className="mr-2">+91</span>
              <input
                type="text"
                maxLength={10}
                value={contactNumber}
                onInput={handleNumberInput}
                placeholder="9999999999"
                className="w-full bg-transparent outline-none"
              />
            </div>
            {phoneError && (
              <p className="text-red-500 text-sm mt-1">{phoneError}</p>
            )}
          </div>
        </div>

        {/* Update Button */}
        <div className="flex justify-center mt-6 p-3">
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-[200px] h-[50px] bg-[#3B0069] text-[18px] hover:bg-[#2a004c] text-white py-3 rounded-[10px] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Updating..." : "Update Vendor"}
            {!loading && (
              <div className="w-8 h-8 rounded-full bg-[#5E2D91] flex items-center justify-center ml-2">
                <ArrowRight className="h-6 w-6 text-white" />
              </div>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdateVendorModal;
